// 一份访问上下文可以经历多轮短凭据；到期/断网不销毁画布，真正撤权才结束上下文。
export function createShareSessionManager ({ accessId, request, validate, onSession, onState = () => {}, onInvalidated = () => {},
  now = () => performance.now(), wallNow = () => Date.now(), later = setTimeout, cancel = clearTimeout, online = () => navigator.onLine !== false, events = window }) {
  let session = null, deadline = 0, browserDeadline = 0, task, inFlight, stopped = false, retry = 1000, lastWall = 0, lastMono = 0
  const clockChanged = () => Math.abs((wallNow() - lastWall) - (now() - lastMono)) > 30000
  const state = value => { if (!stopped) onState(value) }
  const active = () => !stopped && !!session
  const stop = () => { stopped = true; cancel(task); if (request.close) request.close(); for (const event of ['online', 'offline', 'pageshow', 'visibilitychange']) events.removeEventListener(event, wake) }
  const invalidate = message => { if (!stopped) { stop(); onInvalidated(message) } }
  const error = message => Object.assign(new Error(message), { transient: true })
  const arm = (delay, callback) => { cancel(task); if (!stopped) task = later(callback, Math.max(100, delay)) }
  const renewLater = () => {
    if (!session || stopped) return
    if (browserDeadline <= deadline + 100) arm(browserDeadline - now(), () => invalidate('浏览器访问资格已到期，请重新验证'))
    else arm(deadline - now() - 120000, () => renew().catch(() => {}))
  }
  const accept = (value, started) => {
    let access
    try {
      access = validate(value)
      if (value.browserSessionVersion !== 1 || value.accessId !== accessId || !Number.isSafeInteger(value.serverTime) ||
          !Number.isSafeInteger(value.browserExpiresAt) || (session && ['projectId', 'browserId', 'accessId', 'userId', 'controlAvailable', 'browserExpiresAt', 'accessMode', 'authorizationVersion', 'dataAccess'].some(key => value[key] !== session[key]))) throw new Error('浏览器访问身份或权限已变化，请重新验证')
    } catch (cause) { cause.fatal = true; throw cause }
    const expires = started + access.duration, absolute = started + value.browserExpiresAt - value.serverTime
    if (expires <= now() || absolute <= now()) throw error('续期响应已过期，请重试')
    session = value; deadline = expires; browserDeadline = absolute; retry = 1000; lastWall = wallNow(); lastMono = now()
    onSession(value, access)
    state(online() ? 'ready' : 'offline'); renewLater()
    return value
  }
  const run = action => {
    if (stopped) return Promise.reject(error('分享页面已离开或授权失效'))
    if (inFlight) return inFlight
    const started = now()
    const promise = Promise.resolve().then(() => {
      if (!online()) throw error('网络已断开，恢复后自动续期')
      if (session && browserDeadline <= now()) throw Object.assign(new Error('浏览器访问资格已到期，请重新验证'), { status: 401 })
      if (session) state(deadline > now() ? 'renewing' : 'recovering')
      return action()
    }).then(value => { if (stopped) throw error('分享页面已离开'); return accept(value, started) })
      .catch(cause => {
        if (stopped) throw cause
        if (session && (cause.fatal || [401, 403, 404, 409].includes(cause.status))) invalidate(cause.message || '分享授权已失效，请重新验证')
        else if (session) {
          state(!online() ? 'offline' : deadline > now() ? 'retrying' : 'recovering')
          arm(retry, () => renew().catch(() => {})); retry = Math.min(retry * 2, 30000)
        }
        throw cause
      }).finally(() => { if (inFlight === promise) inFlight = null })
    inFlight = promise
    return promise
  }
  const renew = () => run(() => request('_renew', { accessId }))
  const wake = event => {
    if (!active()) return
    if (!online()) { state('offline'); return }
    if (deadline - now() <= 120000 || clockChanged() || (event && ['online', 'pageshow', 'visibilitychange'].includes(event.type))) renew().catch(() => {})
    else state('ready')
  }
  for (const event of ['online', 'offline', 'pageshow', 'visibilitychange']) events.addEventListener(event, wake)
  return {
    get session () { return session },
    active,
    usable: () => active() && online() && deadline > now() && !clockChanged(),
    restore: () => run(() => request('_resume', { accessId })),
    open: (password, remember) => run(() => request('_session', { accessId, password, remember, browserSessionVersion: 1 })),
    renew,
    async remember (remember) {
      if (inFlight) { try { await inFlight } catch (_) { /* 下次请求仍重新校验，不复用失败结果。 */ } }
      return run(() => request('_remember', { accessId, remember }))
    },
    async ensure () {
      if (!active()) throw error('请重新验证分享')
      if (!online()) { state('offline'); throw error('网络已断开，暂不能请求或控制设备') }
      if (deadline <= now() || clockChanged()) await renew()
      else if (deadline - now() <= 120000 && browserDeadline > deadline + 100) renew().catch(() => {})
      if (!active() || deadline <= now()) throw error('访问授权正在恢复，请稍后重试')
      return session
    },
    unauthorized (credential) {
      if (active() && [session.session, session.controlSession].includes(credential)) renew().catch(() => {})
    },
    async forget () { await request('_forget', {}); invalidate('已退出本浏览器的分享访问') },
    stop
  }
}
